import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Box,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
} from "@mui/material";
import AppInfoView from "@crema/core/AppInfoView";
import AppsContainer from "@crema/core/AppsContainer";
import AppsPagination from "@crema/core/AppsPagination";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import { getUsers, getRoles } from "redux/actions/Master";
import UserTable from "./UserTable";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { UserData, RoleData } from "types/models/master/MasterApp";
import { isModuleAuthorized } from "@crema/utility/Utils";

interface MasterState {
  users: UserData[];
  roles: RoleData[];
}

interface AppState {
  master: MasterState;
}

const searchFields = [
  { label: "All", value: "all" },
  { label: "Name", value: "name" },
  { label: "User Name", value: "username" },
  { label: "Email", value: "email" },
  { label: "Role", value: "roleName" },
  { label: "Contact No", value: "contactNo" },
];

const Users = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rowsPerPage = 10;

  const [search, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [page, setPage] = useState(0);

  const users = useSelector<AppState, UserData[]>(
    (state) => state.master.users || []
  );

  const roles = useSelector<AppState, RoleData[]>(
    (state) => state.master.roles || []
  );

  useEffect(() => {
    dispatch(getUsers("", 0));
    dispatch(getRoles());
  }, [dispatch]);

  const handleAddUserClick = () => {
    navigate("/master/manageUser/UserCreate");
  };

  const handleExport = () => {
    const headers = ["Name", "User Name", "SR Code", "Role", "Contact No", "Email", "Status"];
    const rows = filteredUsers.map((u) => [
      u.name ?? "",
      u.username ?? "",
      u.SRCode ?? "",
      u.roleName ?? "",
      u.contactNo ?? "",
      u.email ?? "",
      u.IsActive ? "Active" : "Inactive",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "users.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const onPageChange = (
    event: React.ChangeEvent<unknown> | null,
    value: number
  ) => {
    setPage(value);
  };

  const onSearchUser = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const usersWithRoleName = users.map((user) => ({
    ...user,
    roleName: roles.find((role) => role.id === user.roleId)?.role || "",
  }));

  const filteredUsers = search.trim()
    ? usersWithRoleName.filter((item) => {
        const searchTerm = search.trim().toLowerCase();

        if (searchField === "all") {
          return searchFields
            .filter((f) => f.value !== "all")
            .some(({ value }) => {
              const fieldValue = item[value];
              return fieldValue?.toString().toLowerCase().includes(searchTerm);
            });
        } else {
          const fieldValue = item[searchField];
          return fieldValue?.toString().toLowerCase().includes(searchTerm);
        }
      })
    : usersWithRoleName;

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {messages["sidebar.master.users"]}
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <Button
                variant="outlined"
                color="success"
                onClick={handleExport}
              >
                Export
              </Button>
              {isModuleAuthorized("Add User") && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleAddUserClick}
                >
                  Add User
                </Button>
              )}
            </Box>
          </Box>
        }
        fullView
      >
        <AppsHeader>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 2,
              width: 1,
            }}
          >
            <TextField
              select
              size="small"
              value={searchField}
              onChange={(e) => {
                setSearchField(e.target.value);
                setSearchQuery("");
                setPage(0);
              }}
              sx={{ width: 150 }}
            >
              {searchFields.map((field) => (
                <MenuItem key={field.value} value={field.value}>
                  {field.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              variant="outlined"
              size="small"
              placeholder={messages["common.searchHere"] as string}
              value={search}
              onChange={(e) => onSearchUser(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {search ? (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchQuery("");
                          setPage(0);
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    ) : (
                      <SearchIcon />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{ width: 200 }}
            />
          </Box>
        </AppsHeader>

        <AppsContent
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <UserTable users={paginatedUsers} />
          <AppsPagination
            rowsPerPage={rowsPerPage}
            count={filteredUsers.length}
            page={page}
            onPageChange={onPageChange}
          />
        </AppsContent>
      </AppsContainer>

      <AppInfoView />
    </>
  );
};

export default Users;
